import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { usePortalAuth } from "@/lib/PortalAuthContext";
import { formatPortalError } from "@/lib/portalApi";
import Logo from "@/components/Logo";

export default function PortalLogin() {
  const [mode, setMode] = useState("magic"); // magic | password
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sentLink, setSentLink] = useState(false);
  const { loginWithPassword, requestMagicLink } = usePortalAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      if (mode === "magic") {
        await requestMagicLink(email);
        setSentLink(true);
      } else {
        await loginWithPassword(email, password);
        toast.success("Signed in");
        navigate("/portal");
      }
    } catch (err) {
      toast.error(formatPortalError(err?.response?.data?.detail, "Sign-in failed."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="portal-login-page" className="min-h-screen bg-[#050505] text-white flex flex-col">
      <header className="border-b border-white/10 px-6 md:px-10 h-16 flex items-center">
        <Logo />
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-14">
        <div className="w-full max-w-md">
          <div className="text-[#D4AF37] tracking-[0.24em] text-[11px] mb-3 uppercase">Customer Portal</div>
          <h1 className="font-display text-4xl md:text-5xl leading-tight mb-3" style={{ fontWeight: 300 }}>
            Sign in to <span className="italic text-[#D4AF37]">your account</span>
          </h1>
          <p className="text-white/60 text-sm mb-8">
            Manage your lease, pay instalments, view tolls and update your details.
          </p>

          {sentLink ? (
            <div data-testid="portal-magic-link-sent" className="rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-6">
              <Sparkles className="text-[#D4AF37] mb-3" size={22} />
              <div className="text-white font-medium mb-2">Check your inbox</div>
              <p className="text-white/60 text-sm leading-relaxed">
                If an account exists for <span className="text-white">{email}</span>, we've sent you a
                secure sign-in link. It expires in 20 minutes.
              </p>
              <button
                onClick={() => setSentLink(false)}
                data-testid="portal-magic-link-reset"
                className="mt-5 text-xs tracking-widest text-[#D4AF37] hover:opacity-80"
              >← USE A DIFFERENT EMAIL</button>
            </div>
          ) : (
            <>
              <div className="flex gap-1 mb-6 border-b border-white/10">
                {[
                  { id: "magic", label: "Magic link" },
                  { id: "password", label: "Password" },
                ].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setMode(t.id)}
                    data-testid={`portal-login-tab-${t.id}`}
                    className={`px-4 py-3 text-sm tracking-wide border-b-2 -mb-px transition ${
                      mode === t.id ? "text-[#D4AF37] border-[#D4AF37]" : "text-white/50 border-transparent hover:text-white"
                    }`}
                  >{t.label}</button>
                ))}
              </div>

              <form onSubmit={submit} className="space-y-4">
                <label className="block">
                  <span className="block text-[10px] tracking-widest text-white/50 mb-1.5">EMAIL</span>
                  <div className="relative">
                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      data-testid="portal-login-email"
                      type="email" required
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-[#0a0a0a] border border-white/10 rounded-md pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]/50"
                      placeholder="you@example.com"
                    />
                  </div>
                </label>

                {mode === "password" && (
                  <label className="block">
                    <span className="block text-[10px] tracking-widest text-white/50 mb-1.5">PASSWORD</span>
                    <div className="relative">
                      <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                      <input
                        data-testid="portal-login-password"
                        type="password" required minLength={8}
                        value={password} onChange={(e) => setPassword(e.target.value)}
                        className="w-full bg-[#0a0a0a] border border-white/10 rounded-md pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]/50"
                        placeholder="••••••••"
                      />
                    </div>
                  </label>
                )}

                <button
                  data-testid="portal-login-submit"
                  disabled={loading}
                  className="w-full bg-[#D4AF37] text-[#050505] font-semibold rounded-md py-3 text-sm tracking-widest inline-flex items-center justify-center gap-2 hover:bg-[#E5C158] transition disabled:opacity-50"
                >
                  {loading ? "…" : mode === "magic" ? "SEND MAGIC LINK" : "SIGN IN"}
                  <ArrowRight size={14} />
                </button>
              </form>

              <div className="mt-6 text-xs text-white/40">
                Don't have an account yet? <Link to="/apply" className="text-[#D4AF37] hover:opacity-80">Apply for rent-to-own →</Link>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
